import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import {
  ArrowLeft,
  Building2,
  ClipboardList,
  Plus,
  Tag,
  X,
} from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  AudienceAttributeCatalogEntry,
  CompanyPickerItem,
  TagPickerItem,
} from "@shared-core/data/api-types";
import {
  attributeOptions,
  operatorForAttribute,
  operatorOptions,
} from "@shared-core/domain/audience-rules";
import type {
  AudienceDetail,
  AudiencePreview,
  AudienceRule,
  MarketingPerson,
} from "@shared-core/data/marketing-types";
import { paths } from "@shared-core/routing/routes";
import { MarketingNav } from "../app/components/domain/marketing/MarketingNav";
import { TiersHelpNote } from "../app/components/domain/marketing/TiersHelpNote";
import { consentVariant } from "../app/components/domain/marketing/marketingDisplay";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../app/components/primitives/card";
import { Switch } from "../app/components/primitives/switch";
import { currentTime } from "@shared-core/domain/current-time";

export function AudienceBuilderPage() {
  const { audienceId } = useParams<{ audienceId: string }>();
  const dataAdapter = useDataAdapter();
  const [audience, setAudience] = useState<AudienceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Local rule state for editing; the preview below reflects *saved* rules
  // (the backend resolves persisted state) and refreshes after a save.
  const [rules, setRules] = useState<AudienceRule[]>([]);
  const [savedRules, setSavedRules] = useState<AudienceRule[]>([]);
  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  // The backend's attribute catalog. Empty until loaded (or if it fails) —
  // each rule's select then offers only its own value, so nothing is coerced.
  const [catalog, setCatalog] = useState<AudienceAttributeCatalogEntry[]>([]);

  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .listAudienceAttributeCatalog()
      .then((entries) => {
        if (!cancelled) setCatalog(entries);
      })
      .catch((e) => {
        console.error("marketing.audience.attribute_catalog.load.failed", e);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  // Override (pin in/out) dialog state.
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [overridePeople, setOverridePeople] = useState<MarketingPerson[]>([]);
  const [overrideSearch, setOverrideSearch] = useState("");
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [overrideWorking, setOverrideWorking] = useState<string | null>(null);
  const [bulkVisibleWorking, setBulkVisibleWorking] = useState(false);

  const loadPreview = useCallback(async () => {
    if (!audienceId) return;
    try {
      const p = await dataAdapter.previewMarketingAudience(audienceId);
      setPreview(p);
    } catch (e) {
      console.error("marketing.audience.preview.failed", e);
    }
  }, [dataAdapter, audienceId]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!audienceId) return;
      setLoading(true);
      setError(false);
      try {
        const res = await dataAdapter.getMarketingAudience(audienceId);
        if (!cancelled) {
          setAudience(res);
          setRules(res?.rules ?? []);
          setSavedRules(res?.rules ?? []);
        }
      } catch (e) {
        console.error("marketing.audience.load.failed", e);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    void loadPreview();
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, audienceId, loadPreview]);

  function openOverrideDialog() {
    setOverrideSearch("");
    setShowOverrideDialog(true);
  }

  useEffect(() => {
    if (!showOverrideDialog) return;
    let cancelled = false;
    setOverrideLoading(true);
    void dataAdapter
      .listMarketingPeople({ q: overrideSearch || undefined })
      .then((res) => {
        if (!cancelled) setOverridePeople(res.items);
      })
      .catch((e) => {
        console.error("marketing.audience.override.load.failed", e);
      })
      .finally(() => {
        if (!cancelled) setOverrideLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, showOverrideDialog, overrideSearch]);

  async function handlePin(
    person: MarketingPerson,
    type: "pin_in" | "pin_out",
  ) {
    if (!audienceId) return;
    setOverrideWorking(person.id);
    try {
      await dataAdapter.addMarketingAudienceOverride(
        audienceId,
        person.id,
        type,
      );
      const res = await dataAdapter.getMarketingAudience(audienceId);
      setAudience(res);
    } catch (e) {
      console.error("marketing.audience.override.add.failed", e);
    } finally {
      setOverrideWorking(null);
    }
  }

  async function handleUnpin(person: MarketingPerson) {
    if (!audienceId) return;
    setOverrideWorking(person.id);
    try {
      await dataAdapter.removeMarketingAudienceOverride(audienceId, person.id);
      const res = await dataAdapter.getMarketingAudience(audienceId);
      setAudience(res);
    } catch (e) {
      console.error("marketing.audience.override.remove.failed", e);
    } finally {
      setOverrideWorking(null);
    }
  }

  // Pin/unpin every person currently visible in the override dialog's
  // (search-filtered) list — a bulk convenience alongside the per-person
  // and per-company/tag pin actions.
  async function handlePinAllVisible(type: "pin_in" | "pin_out") {
    if (!audienceId || overridePeople.length === 0) return;
    setBulkVisibleWorking(true);
    try {
      await Promise.all(
        overridePeople.map((p) =>
          dataAdapter.addMarketingAudienceOverride(audienceId, p.id, type),
        ),
      );
      const res = await dataAdapter.getMarketingAudience(audienceId);
      setAudience(res);
      await loadPreview();
    } catch (e) {
      console.error("marketing.audience.override.pin_all_visible.failed", e);
    } finally {
      setBulkVisibleWorking(false);
    }
  }

  async function handleUnpinAllVisible() {
    if (!audienceId || !audience || overridePeople.length === 0) return;
    const pinned = overridePeople.filter(
      (p) =>
        audience.manual_includes.includes(p.id) ||
        audience.manual_excludes.includes(p.id),
    );
    if (pinned.length === 0) return;
    setBulkVisibleWorking(true);
    try {
      await Promise.all(
        pinned.map((p) =>
          dataAdapter.removeMarketingAudienceOverride(audienceId, p.id),
        ),
      );
      const res = await dataAdapter.getMarketingAudience(audienceId);
      setAudience(res);
      await loadPreview();
    } catch (e) {
      console.error("marketing.audience.override.unpin_all_visible.failed", e);
    } finally {
      setBulkVisibleWorking(false);
    }
  }

  // Company bulk picker state.
  const [showCompanyPicker, setShowCompanyPicker] = useState(false);
  const [companies, setCompanies] = useState<CompanyPickerItem[]>([]);
  const [companyPickerLoading, setCompanyPickerLoading] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [bulkWorking, setBulkWorking] = useState<string | null>(null);
  const [emailOnlyFilter, setEmailOnlyFilter] = useState(true);

  function openCompanyPicker() {
    setCompanySearch("");
    setShowCompanyPicker(true);
  }

  useEffect(() => {
    if (!showCompanyPicker) return;
    let cancelled = false;
    setCompanyPickerLoading(true);
    void dataAdapter
      .listCompaniesForOverridePicker(emailOnlyFilter)
      .then((res) => {
        if (!cancelled) setCompanies(res);
      })
      .catch((e) => {
        console.error("marketing.audience.companies.load.failed", e);
      })
      .finally(() => {
        if (!cancelled) setCompanyPickerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, showCompanyPicker, emailOnlyFilter]);

  async function handleBulkPin(
    company: CompanyPickerItem,
    overrideType: "pin_in" | "pin_out",
  ) {
    if (!audienceId) return;
    setBulkWorking(company.company_id);
    try {
      await dataAdapter.bulkAddAudienceOverrides(
        audienceId,
        company.company_id,
        overrideType,
        emailOnlyFilter,
      );
      const res = await dataAdapter.getMarketingAudience(audienceId);
      setAudience(res);
      await loadPreview();
    } catch (e) {
      console.error("marketing.audience.bulk_override.failed", e);
    } finally {
      setBulkWorking(null);
    }
  }

  const filteredCompanies = companySearch
    ? companies.filter((c) =>
        c.company_name.toLowerCase().includes(companySearch.toLowerCase()),
      )
    : companies;

  // Tag bulk picker state — same idea as the company picker, keyed on tag
  // instead of company (e.g. pin out everyone tagged "supplier" in one go).
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [tags, setTags] = useState<TagPickerItem[]>([]);
  const [tagPickerLoading, setTagPickerLoading] = useState(false);
  const [tagSearch, setTagSearch] = useState("");
  const [tagBulkWorking, setTagBulkWorking] = useState<string | null>(null);

  function openTagPicker() {
    setTagSearch("");
    setShowTagPicker(true);
  }

  useEffect(() => {
    if (!showTagPicker) return;
    let cancelled = false;
    setTagPickerLoading(true);
    void dataAdapter
      .listTagsForOverridePicker()
      .then((res) => {
        if (!cancelled) setTags(res);
      })
      .catch((e) => {
        console.error("marketing.audience.tags.load.failed", e);
      })
      .finally(() => {
        if (!cancelled) setTagPickerLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, showTagPicker]);

  async function handleBulkPinByTag(
    tag: TagPickerItem,
    overrideType: "pin_in" | "pin_out",
  ) {
    if (!audienceId) return;
    setTagBulkWorking(tag.tag);
    try {
      await dataAdapter.bulkAddAudienceOverridesByTag(
        audienceId,
        tag.tag,
        overrideType,
        emailOnlyFilter,
      );
      const res = await dataAdapter.getMarketingAudience(audienceId);
      setAudience(res);
      await loadPreview();
    } catch (e) {
      console.error("marketing.audience.bulk_override_by_tag.failed", e);
    } finally {
      setTagBulkWorking(null);
    }
  }

  const filteredTags = tagSearch
    ? tags.filter((t) => t.tag.toLowerCase().includes(tagSearch.toLowerCase()))
    : tags;

  // Pin-by-list state — for an explicit pasted set of contact emails, e.g.
  // reconciling a CSV import whose duplicates didn't pick up a new tag.
  const [showEmailListPicker, setShowEmailListPicker] = useState(false);
  const [emailListText, setEmailListText] = useState("");
  const [emailListWorking, setEmailListWorking] = useState<
    "pin_in" | "pin_out" | null
  >(null);
  // The *Pin by list…* command's summary: how many emails matched a contact,
  // how many were pinned, and how many matched nobody.
  const [emailListResult, setEmailListResult] = useState<string | null>(null);

  function openEmailListPicker() {
    setEmailListText("");
    setEmailListResult(null);
    setShowEmailListPicker(true);
  }

  function parseEmailList(text: string): string[] {
    const seen = new Set<string>();
    for (const raw of text.split(/[\s,;]+/)) {
      const email = raw.trim().toLowerCase();
      if (email.includes("@")) seen.add(email);
    }
    return Array.from(seen);
  }

  const parsedEmails = parseEmailList(emailListText);

  async function handleBulkPinByEmails(overrideType: "pin_in" | "pin_out") {
    if (!audienceId || parsedEmails.length === 0) return;
    setEmailListWorking(overrideType);
    setEmailListResult(null);
    try {
      const result = await dataAdapter.bulkAddAudienceOverridesByEmails(
        audienceId,
        parsedEmails,
        overrideType,
      );
      setEmailListResult(result.summary);
      const res = await dataAdapter.getMarketingAudience(audienceId);
      setAudience(res);
      await loadPreview();
    } catch (e) {
      console.error("marketing.audience.bulk_override_by_emails.failed", e);
    } finally {
      setEmailListWorking(null);
    }
  }

  const [savingRules, setSavingRules] = useState(false);

  const removeRule = (id: string) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const addRule = () => {
    setRules((prev) => [
      ...prev,
      {
        id: `r_${currentTime().getTime()}`,
        kind: "include",
        attribute: "lifecycle_stage",
        operator: "eq",
        value: "",
      },
    ]);
  };

  const updateRule = (id: string, patch: Partial<AudienceRule>) => {
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  // Compares rule content rather than `id` — a save round-trip regenerates
  // ids server-side, so an id-inclusive comparison would never match.
  function ruleContentKey(r: AudienceRule) {
    return `${r.kind}|${r.attribute}|${r.operator}|${r.value}`;
  }
  const rulesUnchanged =
    rules.length === savedRules.length &&
    rules.every((r, i) => ruleContentKey(r) === ruleContentKey(savedRules[i]));

  async function handleSaveRules() {
    if (!audienceId) return;
    setSavingRules(true);
    try {
      const saved = await dataAdapter.saveMarketingAudienceRules(
        audienceId,
        rules,
      );
      setRules(saved);
      setSavedRules(saved);
      // Preview reflects persisted rules — refresh it now they're saved.
      await loadPreview();
    } catch (e) {
      console.error("marketing.audience.save_rules.failed", e);
    } finally {
      setSavingRules(false);
    }
  }

  const [savingExcludeRepeatBouncers, setSavingExcludeRepeatBouncers] =
    useState(false);

  async function handleToggleExcludeRepeatBouncers(next: boolean) {
    if (!audienceId) return;
    setSavingExcludeRepeatBouncers(true);
    const previous = audience?.exclude_repeat_bouncers;
    setAudience((prev) =>
      prev ? { ...prev, exclude_repeat_bouncers: next } : prev,
    );
    try {
      await dataAdapter.updateMarketingAudience(audienceId, {
        exclude_repeat_bouncers: next,
      });
      await loadPreview();
    } catch (e) {
      console.error(
        "marketing.audience.toggle_exclude_repeat_bouncers.failed",
        e,
      );
      setAudience((prev) =>
        prev && previous !== undefined
          ? { ...prev, exclude_repeat_bouncers: previous }
          : prev,
      );
    } finally {
      setSavingExcludeRepeatBouncers(false);
    }
  }

  return (
    <div className="space-y-6">
      <MarketingNav active="audiences" />

      <Link
        to={paths.marketingAudiences()}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to audiences
      </Link>

      {loading && (
        <div className="text-sm text-muted-foreground">Loading audience…</div>
      )}

      {error && !loading && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600">
          Couldn't load this audience.
        </div>
      )}

      {!loading && !error && !audience && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-muted-foreground">
          Audience not found.
        </div>
      )}

      {/* Company bulk picker dialog */}
      {showCompanyPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h2 className="font-semibold text-base">Pin by company</h2>
              <button
                onClick={() => setShowCompanyPicker(false)}
                aria-label="Close"
              >
                <X className="size-5 text-muted-foreground hover:text-foreground" />
              </button>
            </div>
            <div className="px-4 py-3 border-b border-border space-y-2">
              <input
                type="search"
                placeholder="Filter companies…"
                value={companySearch}
                onChange={(e) => setCompanySearch(e.target.value)}
                className="w-full rounded border border-border bg-surface px-3 py-1.5 text-sm"
              />
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailOnlyFilter}
                  onChange={(e) => setEmailOnlyFilter(e.target.checked)}
                  className="rounded"
                />
                Email contacts only
              </label>
            </div>
            <div className="overflow-y-auto max-h-80 divide-y divide-border">
              {companyPickerLoading && (
                <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                  Loading…
                </p>
              )}
              {!companyPickerLoading && filteredCompanies.length === 0 && (
                <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                  No companies found.
                </p>
              )}
              {!companyPickerLoading &&
                filteredCompanies.map((c) => {
                  const busy = bulkWorking === c.company_id;
                  const count = emailOnlyFilter ? c.email_count : c.total_count;
                  return (
                    <div
                      key={c.company_id}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm"
                    >
                      <Building2 className="size-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {c.company_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {count} contact{count !== 1 ? "s" : ""}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => handleBulkPin(c, "pin_in")}
                        >
                          {busy ? "…" : "Pin in"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy}
                          onClick={() => handleBulkPin(c, "pin_out")}
                        >
                          {busy ? "…" : "Pin out"}
                        </Button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Tag bulk picker dialog */}
      {showTagPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h2 className="font-semibold text-base">Pin by tag</h2>
              <button
                onClick={() => setShowTagPicker(false)}
                aria-label="Close"
              >
                <X className="size-5 text-muted-foreground hover:text-foreground" />
              </button>
            </div>
            <div className="px-4 py-3 border-b border-border space-y-2">
              <input
                type="search"
                placeholder="Filter tags…"
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
                className="w-full rounded border border-border bg-surface px-3 py-1.5 text-sm"
              />
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailOnlyFilter}
                  onChange={(e) => setEmailOnlyFilter(e.target.checked)}
                  className="rounded"
                />
                Email contacts only
              </label>
            </div>
            <div className="overflow-y-auto max-h-80 divide-y divide-border">
              {tagPickerLoading && (
                <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                  Loading…
                </p>
              )}
              {!tagPickerLoading && filteredTags.length === 0 && (
                <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                  No tags found.
                </p>
              )}
              {!tagPickerLoading &&
                filteredTags.map((t) => {
                  const busy = tagBulkWorking === t.tag;
                  return (
                    <div
                      key={t.tag}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm"
                    >
                      <Tag className="size-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {t.tag}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => handleBulkPinByTag(t, "pin_in")}
                        >
                          {busy ? "…" : "Pin in"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy}
                          onClick={() => handleBulkPinByTag(t, "pin_out")}
                        >
                          {busy ? "…" : "Pin out"}
                        </Button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Pin-by-list dialog */}
      {showEmailListPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h2 className="font-semibold text-base">Pin by list</h2>
              <button
                onClick={() => setShowEmailListPicker(false)}
                aria-label="Close"
              >
                <X className="size-5 text-muted-foreground hover:text-foreground" />
              </button>
            </div>
            <div className="px-4 py-3 space-y-3">
              <p className="text-xs text-muted-foreground">
                Paste a list of contact emails (one per line, or comma/space
                separated) to pin them into this audience directly — useful when
                a re-import left some existing contacts without a new tag.
              </p>
              <textarea
                placeholder="alice@example.com&#10;bob@example.com"
                value={emailListText}
                onChange={(e) => {
                  setEmailListText(e.target.value);
                  setEmailListResult(null);
                }}
                rows={8}
                className="w-full rounded border border-border bg-surface px-3 py-1.5 text-sm font-mono"
              />
              <p className="text-xs text-muted-foreground">
                {parsedEmails.length} email
                {parsedEmails.length !== 1 ? "s" : ""} recognised
              </p>
              {emailListResult && (
                <p className="text-xs text-foreground">{emailListResult}</p>
              )}
              <div className="flex gap-2 justify-end">
                <Button
                  size="sm"
                  disabled={
                    parsedEmails.length === 0 || emailListWorking !== null
                  }
                  onClick={() => handleBulkPinByEmails("pin_in")}
                >
                  {emailListWorking === "pin_in" ? "…" : "Pin in"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={
                    parsedEmails.length === 0 || emailListWorking !== null
                  }
                  onClick={() => handleBulkPinByEmails("pin_out")}
                >
                  {emailListWorking === "pin_out" ? "…" : "Pin out"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Override dialog */}
      {showOverrideDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h2 className="font-semibold text-base">Pin / unpin people</h2>
              <button
                onClick={() => setShowOverrideDialog(false)}
                aria-label="Close"
              >
                <X className="size-5 text-muted-foreground hover:text-foreground" />
              </button>
            </div>
            <div className="px-4 py-3 border-b border-border space-y-2">
              <input
                type="search"
                placeholder="Search contacts…"
                value={overrideSearch}
                onChange={(e) => setOverrideSearch(e.target.value)}
                className="w-full rounded border border-border bg-surface px-3 py-1.5 text-sm"
              />
              {overridePeople.length > 0 && (
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-muted-foreground">
                    Applies to all {overridePeople.length} contact
                    {overridePeople.length !== 1 ? "s" : ""} shown below.
                  </p>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={bulkVisibleWorking}
                      onClick={() => handlePinAllVisible("pin_in")}
                    >
                      {bulkVisibleWorking ? "…" : "Pin all"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={bulkVisibleWorking}
                      onClick={handleUnpinAllVisible}
                    >
                      {bulkVisibleWorking ? "…" : "Unpin all"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="overflow-y-auto max-h-80 divide-y divide-border">
              {overrideLoading && (
                <p className="px-4 py-6 text-sm text-muted-foreground text-center">
                  Loading…
                </p>
              )}
              {!overrideLoading &&
                overridePeople.map((p) => {
                  const isPinnedIn = audience?.manual_includes.includes(p.id);
                  const isPinnedOut = audience?.manual_excludes.includes(p.id);
                  const busy = overrideWorking === p.id;
                  return (
                    <div
                      key={p.id}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {p.name}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {p.company_name ?? p.email ?? ""}
                        </p>
                      </div>
                      {isPinnedIn || isPinnedOut ? (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy}
                          onClick={() => handleUnpin(p)}
                          className="shrink-0"
                        >
                          {busy ? "…" : `Unpin (${isPinnedIn ? "in" : "out"})`}
                        </Button>
                      ) : (
                        <div className="flex gap-2 shrink-0">
                          <Button
                            size="sm"
                            disabled={busy}
                            onClick={() => handlePin(p, "pin_in")}
                          >
                            {busy ? "…" : "Pin in"}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busy}
                            onClick={() => handlePin(p, "pin_out")}
                          >
                            {busy ? "…" : "Pin out"}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {!loading && !error && audience && (
        <>
          <div>
            <h1 className="text-2xl mb-1">{audience.name}</h1>
            <p className="text-sm text-muted-foreground">
              {audience.description}
            </p>
            <div className="mt-2">
              <TiersHelpNote />
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
            {/* LEFT — rule editor */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base">
                      Membership rules
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={addRule}>
                        <Plus className="size-4" />
                        Add rule
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveRules}
                        disabled={savingRules || rulesUnchanged}
                      >
                        {savingRules
                          ? "Saving…"
                          : rulesUnchanged
                            ? "Saved"
                            : "Save rules"}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {rules.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No rules yet — this audience is curated manually.
                    </p>
                  )}
                  {rules.map((r) => (
                    <div
                      key={r.id}
                      className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm"
                    >
                      <select
                        value={r.kind}
                        onChange={(e) =>
                          updateRule(r.id, {
                            kind: e.target.value as "include" | "exclude",
                          })
                        }
                        className="rounded border border-border bg-background px-2 py-1 text-xs"
                        aria-label="Rule type"
                      >
                        <option value="include">include</option>
                        <option value="exclude">exclude</option>
                      </select>
                      <select
                        value={r.attribute}
                        onChange={(e) =>
                          updateRule(r.id, {
                            attribute: e.target.value,
                            operator: operatorForAttribute(
                              catalog,
                              e.target.value,
                              r.operator,
                            ),
                          })
                        }
                        className="rounded border border-border bg-background px-2 py-1 text-xs"
                        aria-label="Attribute"
                      >
                        {attributeOptions(catalog, r.attribute).map((a) => (
                          <option key={a.value} value={a.value}>
                            {a.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={r.operator}
                        onChange={(e) =>
                          updateRule(r.id, { operator: e.target.value })
                        }
                        className="rounded border border-border bg-background px-2 py-1 text-xs"
                        aria-label="Operator"
                      >
                        {operatorOptions(catalog, r.attribute, r.operator).map(
                          (o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ),
                        )}
                      </select>
                      <input
                        value={r.value}
                        onChange={(e) =>
                          updateRule(r.id, { value: e.target.value })
                        }
                        placeholder="value"
                        className="rounded border border-border bg-background px-2 py-1 text-xs w-28"
                        aria-label="Value"
                      />
                      <button
                        onClick={() => removeRule(r.id)}
                        className="ml-auto text-muted-foreground hover:text-foreground"
                        aria-label="Remove rule"
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Settings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm">Exclude repeat bouncers</p>
                      <p className="text-sm text-muted-foreground">
                        Leaves out contacts who have bounced on 2 or more past
                        campaigns. On by default — turn off only if this
                        audience should deliberately include them.
                      </p>
                    </div>
                    <Switch
                      checked={audience.exclude_repeat_bouncers}
                      disabled={savingExcludeRepeatBouncers}
                      onCheckedChange={handleToggleExcludeRepeatBouncers}
                      aria-label="Exclude repeat bouncers"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Manual curation</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <p className="text-muted-foreground mb-1">
                      Pinned in ({audience.manual_includes.length})
                    </p>
                    <p className="text-foreground">
                      {audience.manual_includes.length === 0
                        ? "None"
                        : audience.manual_includes.join(", ")}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground mb-1">
                      Pinned out ({audience.manual_excludes.length})
                    </p>
                    <p className="text-foreground">
                      {audience.manual_excludes.length === 0
                        ? "None"
                        : audience.manual_excludes.join(", ")}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openOverrideDialog}
                    >
                      <Plus className="size-4" />
                      Add / remove people
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openCompanyPicker}
                    >
                      <Building2 className="size-4" />
                      Pin by company
                    </Button>
                    <Button variant="outline" size="sm" onClick={openTagPicker}>
                      <Tag className="size-4" />
                      Pin by tag
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openEmailListPicker}
                    >
                      <ClipboardList className="size-4" />
                      Pin by list
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* RIGHT — preview (reflects saved rules) */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Preview</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Resolved from saved rules — save to refresh
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded-lg bg-primary/10 py-3">
                      <div className="text-2xl font-semibold text-primary">
                        {preview?.in_count}
                      </div>
                      <div className="text-xs text-muted-foreground">In</div>
                    </div>
                    <div className="rounded-lg bg-surface border border-border py-3">
                      <div className="text-2xl font-semibold text-foreground">
                        {preview?.out_count}
                      </div>
                      <div className="text-xs text-muted-foreground">Out</div>
                    </div>
                    <div className="rounded-lg bg-red-100 py-3">
                      <div className="text-2xl font-semibold text-red-800">
                        {preview?.suppressed_count}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Suppressed
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {audience.sample_members.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Sample members</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {audience.sample_members.map((p) => (
                      <div
                        key={p.id}
                        className="border-b border-border pb-3 last:border-0 last:pb-0"
                      >
                        <div className="font-medium text-foreground text-sm">
                          {p.name}
                        </div>
                        <div className="text-xs text-muted-foreground mb-1.5">
                          {p.job_title ?? "—"}
                          {p.company_name ? ` · ${p.company_name}` : ""}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {p.consent.map((c) => (
                            <Badge
                              key={c.channel}
                              variant={consentVariant(c.state)}
                              className="text-[10px]"
                            >
                              {c.channel}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
